import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../../components/Avatar';
import Box from '@mui/material/Box';
import { IconButton } from '../../components/IconButton';
import { Menu } from '../../components/Menu';
import { MenuItem } from '../../components/Menu';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';

import { visibleUserMenuItems, UserMenuItem } from './core/user-menu-items';
import { NAVBAR_ICON_SIZE } from './navbar-icon-size';

export interface UserMenuProps {
  personName: string | null;
  personPhoto: string | null;
  showMyData: boolean;
  showSettings: boolean;
  showSelectAccount: boolean;
  showChangePassword: boolean;
  onNavigate: (path: string) => void;
  onLogout: () => void;
}

// Tamanho do gatilho quando há foto: o Avatar fica maior que NAVBAR_ICON_SIZE de
// propósito, para a foto não ficar minúscula — o ícone de fallback usa o tamanho
// padrão da barra, não este valor, então o gatilho muda de dimensão conforme a
// pessoa tem ou não foto.
const TRIGGER_SIZE = 32;

/**
 * Apresentação pura: foto/nome da pessoa logada e o menu com os itens visíveis
 * (decididos fora, no view-model) mais o logout. Sem usuário/pessoa
 * (integrador/distribuidor ou sessão ausente), ainda assim expõe o logout —
 * sair da aplicação não deve depender de haver pessoa vinculada.
 *
 * Sem foto, cai num ícone genérico (e não nas iniciais) para ficar visualmente
 * consistente com os outros gatilhos-ícone da barra (conta e idioma).
 */
export function UserMenu({
  personName,
  personPhoto,
  showMyData,
  showSettings,
  showSelectAccount,
  showChangePassword,
  onNavigate,
  onLogout,
}: UserMenuProps): JSX.Element {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const items = visibleUserMenuItems({
    myData: showMyData,
    settings: showSettings,
    selectAccount: showSelectAccount,
    changePassword: showChangePassword,
  });

  function handleOpen(event: MouseEvent<HTMLElement>): void {
    setAnchorEl(event.currentTarget);
  }

  function handleClose(): void {
    setAnchorEl(null);
  }

  function handleSelect(item: UserMenuItem): void {
    // Fecha antes do efeito para o menu não ficar aberto sobre a navegação/logout.
    handleClose();
    if (item.path === null) {
      onLogout();
      return;
    }
    onNavigate(item.path);
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <IconButton
        onClick={handleOpen}
        color="inherit"
        aria-label="Menu do usuário"
        aria-controls={isOpen ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {personPhoto ? (
          <Avatar
            src={personPhoto}
            alt={personName ?? undefined}
            sx={{ width: TRIGGER_SIZE, height: TRIGGER_SIZE }}
          />
        ) : (
          <AccountCircleOutlinedIcon color="inherit" sx={{ fontSize: NAVBAR_ICON_SIZE + 4 }} />
        )}
      </IconButton>
      <Menu id="user-menu" anchorEl={anchorEl} open={isOpen} onClose={handleClose}>
        {items.map((item) => (
          <MenuItem key={item.key} onClick={() => handleSelect(item)}>
            {t(item.labelKey)}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
